import {
  Directive,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { map, Subject, takeUntil } from 'rxjs';
import {
  DEFAULT_ROLES_TO_HIDE_LIST,
  UserType,
} from 'src/app/core/helpers/global/global.constant';
import { TYPES_USER_PERMISSIONS } from 'src/app/core/helpers/global/user-management.constant';
import { GlobalService } from 'src/app/core/services/ui/global.service';

/**
 * Directiva estructural para ocultar contenido a ciertos tipos de usuario.
 *
 * - Por defecto, OCULTA para: agent, collaboratorAgent, collaboratorOperator.
 * - Admin siempre ve todo (no está en la lista por defecto).
 *
 * Uso:
 *   <div *appHideForUserTypes>
 *     Solo se muestra si el userType NO está en la lista por defecto.
 *   </div>
 *
 *   <button *appHideForUserTypes="['agent']">
 *     Visible para todos excepto 'agent'.
 *   </button>
 *
 *   <section *appHideForUserTypes="'collaboratorOperator'">
 *     Visible para todos excepto 'collaboratorOperator'.
 *   </section>
 */

@Directive({
  selector: '[appHideForUserTypes]',
})
export class ShowForUserTypesDirective implements OnInit, OnDestroy {
  private unsubscribe$ = new Subject<void>();

  private denied: readonly UserType[] = DEFAULT_ROLES_TO_HIDE_LIST;
  private currentUserType: UserType = '' as UserType;
  private isShown = false;

  @Input('appHideForUserTypes')
  set appHideForUserTypes(value: UserType[] | UserType | undefined | null) {
    if (Array.isArray(value)) {
      this.denied = value;
    } else if (typeof value === 'string' && value.trim()) {
      this.denied = [value];
    } else {
      this.denied = DEFAULT_ROLES_TO_HIDE_LIST;
    }

    this.evaluate();
  }

  constructor(
    private _globalService: GlobalService,
    private _templateRef: TemplateRef<any>,
    private _viewContainerRef: ViewContainerRef
  ) {}

  ngOnInit(): void {
    this.loadUserRoleWhithPermissions();
  }

  loadUserRoleWhithPermissions(): void {
    this._globalService.profile
      .pipe(
        takeUntil(this.unsubscribe$),
        map((user) => (user?.userType ?? '').toString().trim().toLowerCase())
      )
      .subscribe((userType) => {
        this.currentUserType = userType as UserType;
        this.evaluate();
      });
  }

  private evaluate() {
    const userTypeCurrent = (this.currentUserType || '')
      .toString()
      .toLowerCase();

    if (userTypeCurrent === TYPES_USER_PERMISSIONS.ADMIN) {
      this.ensureShown();
      return;
    }

    const deny = this.denied.map((deny) => deny.toString().toLowerCase());
    const mustHide = deny.includes(userTypeCurrent);

    if (mustHide) this.ensureHidden();
    else this.ensureShown();
  }

  private ensureShown() {
    if (!this.isShown) {
      this._viewContainerRef.clear();
      this._viewContainerRef.createEmbeddedView(this._templateRef);
      this.isShown = true;
    }
  }

  private ensureHidden() {
    if (this.isShown) {
      this._viewContainerRef.clear();
      this.isShown = false;
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
